import type { AnalysisResult } from "@/types/analysis";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import {
  Info,
  Search,
  Settings,
  FileText,
  Bot,
  Key,
  Link2,
  TrendingUp,
  Zap,
} from "lucide-react";

const RadialGauge = ({ score, size = 120 }: { score: number; size?: number }) => {
  const r = (size - 12) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 70 ? "hsl(var(--success))" : score >= 40 ? "hsl(var(--warning))" : "hsl(var(--destructive))";

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="6" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-3xl font-bold text-foreground">{score}</span>
      </div>
    </div>
  );
};

const getQualityLabel = (score: number) => {
  if (score >= 80) return { text: "Excellent", className: "text-success" };
  if (score >= 60) return { text: "Good", className: "text-success" };
  if (score >= 40) return { text: "Needs Work", className: "text-warning" };
  return { text: "Poor", className: "text-destructive" };
};

const getProgressColor = (score: number) => {
  if (score >= 70) return "[&>div]:bg-success";
  if (score >= 40) return "[&>div]:bg-warning";
  return "[&>div]:bg-destructive";
};

interface MetricCardProps {
  icon: React.ElementType;
  label: string;
  score?: number;
  subtitle?: string;
  children?: React.ReactNode;
}

const MetricCard = ({ icon: Icon, label, score, subtitle, children }: MetricCardProps) => (
  <Card className="border-border bg-card hover:bg-accent/30 transition-colors">
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        {score !== undefined && (
          <span className="text-lg font-bold text-foreground">{score}</span>
        )}
      </div>
      {score !== undefined && (
        <Progress value={score} className={`h-1.5 mb-2 ${getProgressColor(score)}`} />
      )}
      {children}
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      {subtitle && (
        <p className="text-[10px] text-muted-foreground/70 truncate mt-0.5">{subtitle}</p>
      )}
    </CardContent>
  </Card>
);

export const ScoreOverview = ({ result }: { result: AnalysisResult }) => {
  const quality = getQualityLabel(result.overallScore);
  const highImpactActions = result.actionPlan.filter((a) => a.impact === "high").length;

  return (
    <Card className="border-border bg-card">
      <CardContent className="p-6 md:p-8">
        {/* Hero: Overall Score */}
        <div className="flex flex-col sm:flex-row items-center gap-6 mb-6">
          <RadialGauge score={result.overallScore} size={140} />
          <div className="text-center sm:text-left">
            <h2 className="text-lg font-semibold text-foreground">Overall Visibility</h2>
            <p className={`text-sm font-medium ${quality.className}`}>{quality.text}</p>
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="text-[10px] py-0 px-1.5 gap-1 mt-2">
                  <Info className="w-2.5 h-2.5" />
                  {result.overallConfidence}% confidence
                </Badge>
              </TooltipTrigger>
              <TooltipContent>Confidence based on data completeness</TooltipContent>
            </Tooltip>
            <div className="mt-2 text-xs text-muted-foreground">
              <span className="text-foreground font-mono">{result.url}</span>
              <span className="mx-1">·</span>
              {new Date(result.analyzedAt).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Metric Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <MetricCard icon={Search} label="On-Page SEO" score={result.seoAudit.onPage.score} />
          <MetricCard icon={Settings} label="Technical SEO" score={result.seoAudit.technical.score} />
          <MetricCard icon={FileText} label="Content Quality" score={result.seoAudit.contentQuality.score} />
          <MetricCard icon={Bot} label="AI Visibility" score={result.aiVisibility.overallScore} />

          {result.keywordAnalysis && (
            <MetricCard
              icon={Key}
              label="Keyword Optimization"
              score={result.keywordAnalysis.overallScore}
              subtitle={result.keywordAnalysis.primaryKeyword}
            />
          )}

          {result.linkIntelligence && (() => {
            const li = result.linkIntelligence!;
            const authorityScore = li.estimatedReferringDomainsForPage1 > 0
              ? Math.min(100, Math.round((li.currentEstimatedReferringDomains / li.estimatedReferringDomainsForPage1) * 100))
              : 100;
            const factors = [li.internalLinkingScore, authorityScore];
            if (li.externalLinkQualityScore != null) factors.push(li.externalLinkQualityScore);
            const linkScore = Math.round(factors.reduce((a, b) => a + b, 0) / factors.length);
            return (
              <MetricCard
                icon={Link2}
                label="Link Intelligence"
                score={linkScore}
                subtitle={`${li.internalLinks} internal · ${li.externalLinks} external`}
              />
            );
          })()}

          {/* Ranking — no progress bar */}
          <Card className="border-border bg-card hover:bg-accent/30 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-sm font-bold text-muted-foreground">
                  #{result.rankingPrediction.currentRangeMin}-{result.rankingPrediction.currentRangeMax}
                </span>
                <span className="text-muted-foreground/50">→</span>
                <span className="text-sm font-bold text-success">
                  #{result.rankingPrediction.achievableRangeMin}-{result.rankingPrediction.achievableRangeMax}
                </span>
              </div>
              <p className="text-xs font-medium text-muted-foreground">Ranking Estimate</p>
            </CardContent>
          </Card>

          {/* Action Plan — count badge */}
          <Card className="border-border bg-card hover:bg-accent/30 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Zap className="w-4 h-4 text-muted-foreground" />
                <Badge variant="default" className="text-[10px] py-0 px-1.5">
                  {highImpactActions} high
                </Badge>
              </div>
              <p className="text-lg font-bold text-foreground mb-1">{result.actionPlan.length}</p>
              <p className="text-xs font-medium text-muted-foreground">Action Items</p>
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
};
